/** Shared helpers for SimpleProductPage variants: no static product datasets. */
import { amazonPriceIsExpired } from '../../domain/amazonPricePolicy';
import { getTrackedAmazonLink } from '../../domain/amazonTrackedLinks';
import { compareRetailerPreference } from '../../domain/retailerPreference';

export const PRICE_GREEN = '#22c55e';
export const PRICE_GREEN_DARK = '#16a34a';
export const SAVINGS_ORANGE = '#e67e22';

/** Matches :root --font-sans in index.css (Roboto/system stack used site-wide). */
export const SITE_FONT_SANS =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

export const getDisplayImageSrc = (url) => {
  if (!url) return '';
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${process.env.PUBLIC_URL || ''}${path}`;
};

export const isAmpedOutdoorsRetailer = (retailerName) =>
  Boolean(retailerName && retailerName.toLowerCase().includes('amped outdoors'));

export const getComparableOfferPrice = (offer, { isAmpedOutdoors = false } = {}) => {
  if (!offer) return null;
  if (amazonPriceIsExpired(offer)) return null;
  let price = offer.numeric_price ?? offer.current_price;
  if (typeof price !== 'number' || !Number.isFinite(price)) {
    price = parseFloat(String(offer.Price || '').replace(/[$,]/g, ''));
  }
  if (!Number.isFinite(price) || price <= 0) return null;

  const retailerName = offer.sourceDisplay || offer.Source || offer.website || '';
  if (isAmpedOutdoors && isAmpedOutdoorsRetailer(retailerName)) {
    return Math.round(price * 0.9 * 100) / 100;
  }
  return price;
};

export const pricesMatch = (a, b) => {
  if (a == null || b == null) return false;
  return Math.round(a * 100) === Math.round(b * 100);
};

export const getActiveOffers = (offers) => {
  if (!Array.isArray(offers)) return [];
  return offers.filter((item) => getComparableOfferPrice(item) != null && item?.is_active !== false);
};

export const getLowestOffer = (offers, options = {}) => {
  const active = getActiveOffers(offers);
  if (!active.length) return null;
  return [...active].sort(
    (a, b) => (getComparableOfferPrice(a, options) ?? Infinity) - (getComparableOfferPrice(b, options) ?? Infinity)
      || compareRetailerPreference(getRetailerNameFromOffer(a), getRetailerNameFromOffer(b))
  )[0];
};

export const getTiedLowestOffers = (sortedOffers, options = {}) => {
  if (!Array.isArray(sortedOffers) || !sortedOffers.length) return [];
  const lowestPrice = getComparableOfferPrice(sortedOffers[0], options);
  if (lowestPrice == null) return [];
  return sortedOffers.filter((offer) =>
    pricesMatch(getComparableOfferPrice(offer, options), lowestPrice)
  );
};

export const getOfferLink = (offer) => {
  if (!offer) return '';
  const affiliate =
    offer.affiliate_url ||
    offer['Job Link'] ||
    offer.affiliate_link ||
    '';
  if (affiliate) return getTrackedAmazonLink(affiliate);
  return offer.retailer_product_url || '';
};

export const getRetailerNameFromOffer = (offer) =>
  offer?.sourceDisplay || offer?.Source || offer?.website || 'Unknown';

export const formatLowestPriceRetailerCopy = (offers) => {
  const names = [];
  const seen = new Set();
  for (const offer of offers || []) {
    const name = getRetailerNameFromOffer(offer);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }

  if (names.length === 0) return '';
  if (names.length === 1) return `Lowest price at ${names[0]}`;
  if (names.length === 2) return `Lowest price at ${names[0]} and ${names[1]}`;
  return `Lowest price at ${names[0]}, ${names[1]}, and ${names.length - 2} more`;
};

export const formatHeroCurrency = (value) => {
  if (value == null || value === '') return null;
  const num =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/[$,]/g, ''));
  if (!Number.isFinite(num)) return null;
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const computeMsrpSavings = (msrp, price) => {
  if (msrp == null || price == null) return null;
  const msrpNum = typeof msrp === 'number' ? msrp : parseFloat(msrp);
  const priceNum = typeof price === 'number' ? price : parseFloat(price);
  if (!Number.isFinite(msrpNum) || !Number.isFinite(priceNum)) return null;
  if (msrpNum <= 0 || priceNum <= 0 || priceNum >= msrpNum) return null;
  return msrpNum - priceNum;
};
